/* istanbul ignore file */
/**
 * Created by gboue on 8/30/22
 * File: ProvideSAMLMetadata
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { VFC, useCallback, useContext, useMemo, useRef, useState, EventHandler, useEffect } from 'react'
import { Body, Bold, Button, ModalFooter, Title } from '@panwds/react-ui'
import { PaperclipIcon } from '@panwds/icons'
import {
  BUTTON_FINISH,
  BUTTON_PREVIOUS,
  BUTTON_UPLOAD_CERT,
  CERTIFICATE_SUCCESS_UPLOADED,
  ERROR_IDP_UPLOAD,
  ERROR_INVALID_URL,
  LABEL_IDP_CERT,
  LABEL_IDP_CONFIG_TYPE,
  LABEL_IDP_ID,
  LABEL_IDP_LOGIN_URL,
  PLACEHOLDER_SAML_LOGIN_URL,
  PLACEHOLDER_SAML_METADATA_ID,
  TITLE_CONFIG_SAML,
} from '../../../constants/LabelConstants'
import { Form, SubmitButton, Input } from '@panwds/react-form'
import { useFormContext } from 'react-hook-form'
import { DATA_TEST_META_PREVIOUS_BUTTON } from '../../../../common/constants/TestingDataIds'
import GlobalContext from '../../../../common/context/GlobalContext'
import { CreateFederationIdp, UpdateFederationIdp } from '../../../actions/IAMActions'
import ScreenLoader from '../../../../common/components/screenLoader/ScreenLoader'
import { formatCert } from '../../../utils/certificate'
import type { ISamlMetadata } from '../cards/ProviderCard'
import isURL from 'validator/lib/isURL'

const FormContent: VFC<{
  error: any,
  uploadedFileName: string,
  samlMetadata: ISamlMetadata | null,
  handleCertificateUpload: EventHandler<any>,
  handleCertificateUploadChange: EventHandler<any>,
  hiddenCertificateInput: any,
  loading: boolean,
  idpData?: any,
  uploadedCert: string,
  onBackPressed: EventHandler<any>,
}> = ({ uploadedCert, onBackPressed, error, uploadedFileName, samlMetadata, handleCertificateUpload, handleCertificateUploadChange, hiddenCertificateInput, loading, idpData }) => {
  const { trigger, setValue, formState: { isValid } } = useFormContext()

  useEffect(() => {
    if (idpData?.config) {
      const { entityId, ssoServiceUrl } = idpData.config
      setValue('identity_provider_id', entityId, { shouldDirty: true })
      setValue('identity_provider_login_url', ssoServiceUrl, { shouldDirty: true })
    }
    if (samlMetadata) {
      const { identityProviderId, loginUrl } = samlMetadata
      setValue('identity_provider_id', identityProviderId, { shouldDirty: true })
      setValue('identity_provider_login_url', loginUrl, { shouldDirty: true })
    }
  }, [samlMetadata, setValue, idpData])

  return (
    <div className='tw-flex tw-flex-col tw-absolute tw-inset-0 tw-overflow-auto tw-p-4 tw-gap-2' >
      <Title level={1} size='lg' addClassName='tw-font-semibold'>{TITLE_CONFIG_SAML}</Title>
      {/* Upload Certificate */}
      <div className='tw-flex tw-flex-col tw-gap-2 tw-w-max'>
        <Body size='sm' addClassName="tw-pb-1"><Bold>{LABEL_IDP_CERT}</Bold></Body>
        <div>
          <Button onClick={handleCertificateUpload} appearance='secondary' icon={<PaperclipIcon size='sm' />}>{BUTTON_UPLOAD_CERT}</Button>
        </div>
        <input type="file" accept=".crt" onChange={e => handleCertificateUploadChange(e?.currentTarget?.files?.[0])} ref={hiddenCertificateInput} style={{ display: 'none' }} />
        {uploadedFileName && <Body size='xs' addClassName='tw-text-gray-600 tw-italic'>{CERTIFICATE_SUCCESS_UPLOADED}</Body>}
      </div>

      {/* Provider ID */}
      <div className='tw-flex tw-flex-col tw-gap-2'>
        <Body size='sm'><Bold>{LABEL_IDP_ID}</Bold></Body>
        <Input
          required
          name={'identity_provider_id'}
          label={LABEL_IDP_ID}
          showLabel={false}
          disabled={loading}
          placeholder={PLACEHOLDER_SAML_METADATA_ID}
          register={{
            onChange: async () => await trigger('identity_provider_id'),
            value: samlMetadata?.identityProviderId
          }}
        />
      </div>

      {/* Login URL */}
      <div className='tw-flex tw-flex-col tw-gap-2'>
        <Body size='sm'><Bold>{LABEL_IDP_LOGIN_URL}</Bold></Body>
        <Input
          required
          name={'identity_provider_login_url'}
          label={LABEL_IDP_LOGIN_URL}
          showLabel={false}
          placeholder={PLACEHOLDER_SAML_LOGIN_URL}
          disabled={loading}
          register={{
            onChange: async () => await trigger('identity_provider_login_url'),
            validate: value => isURL(value) || ERROR_INVALID_URL,
            value: samlMetadata?.loginUrl
          }}
        />
      </div>

      {/* Provider SSO URL */}
      {/*<div className='tw-flex tw-flex-col tw-gap-2'>*/}
      {/*  <Body size='sm'><Bold>{LABEL_IDP_SSO_URL}</Bold></Body>*/}
      {/*  <Input*/}
      {/*    required*/}
      {/*    name={'identity_provider_sso_url'}*/}
      {/*    label={LABEL_IDP_SSO_URL}*/}
      {/*    showLabel={false}*/}
      {/*    disabled={loading}*/}
      {/*    placeholder={PLACEHOLDER_SAML_SSO_URL}*/}
      {/*    register={{*/}
      {/*      onChange: async () => await trigger('identity_provider_sso_url'),*/}
      {/*      validate: value => isURL(value) || ERROR_INVALID_URL,*/}
      {/*      value: samlMetadata?.ssoUrl*/}
      {/*    }}*/}
      {/*  />*/}
      {/*</div>*/}

      {error && <Body size='md' addClassName='tw-text-red tw-italic tw-pt-1'>{ERROR_IDP_UPLOAD}</Body>}

      <ModalFooter addClassName='tw-absolute' style={{ right: 0, left: 0, bottom: 0 }}>
        <Button
          onClick={onBackPressed}
          appearance="secondary"
          disabled={loading}
          dataTestId={DATA_TEST_META_PREVIOUS_BUTTON}
        >{BUTTON_PREVIOUS}</Button>
        <SubmitButton
          disabled={loading || !uploadedCert || !isValid }
        >
          {BUTTON_FINISH}
        </SubmitButton>
      </ModalFooter>
    </div>
  )
}

const ProvideSAMLMetadata: VFC<{
  uploadedFileName: string,
  samlMetadata: ISamlMetadata | null,
  loading: boolean,
  onBackPressed: EventHandler<any>,
  fetchData: Function,
  setLoading: Function,
  setUploadedFileName: Function,
  domain: string,
  closeModal: Function
  idpData: any
  isEdit: boolean
  idpId?: string
}> = ({ setUploadedFileName, uploadedFileName, samlMetadata, loading, onBackPressed, fetchData, setLoading, domain, closeModal, idpData, isEdit, idpId }) => {
  const [error, setError] = useState(false)
  const [uploadedCert, setUploadedCert] = useState<string>('')
  const hiddenCertificateInput = useRef<{ click: Function }>(null)
  const fileReader = useMemo(() => new FileReader(), [])
  const { treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer

  useEffect(() => {
    if (isEdit && idpData?.config?.certificates[0]) {
      setUploadedCert(idpData?.config?.certificates[0])
    }
    if (samlMetadata?.certificate) {
      setUploadedCert(samlMetadata?.certificate)
    }
  }, [idpData?.config?.certificates, samlMetadata?.certificate, isEdit])

  const handleCertificateUpload = useCallback(event => {
    hiddenCertificateInput?.current?.click()
  }, [])

  const handleFileRead = useCallback((e) => {
    const content = fileReader.result as string
    setUploadedCert(content)
  }, [setUploadedCert, fileReader])

  const handleCertificateUploadChange = useCallback((file) => {
    setUploadedFileName(file.name)
    fileReader.onloadend = handleFileRead
    fileReader.readAsText(file)
  }, [fileReader, handleFileRead, setUploadedFileName])

  const onHandleEdit = useCallback(async ({ identity_provider_id: entityId, identity_provider_login_url: ssoServiceUrl }) => {
    const tsg_id = selectedTreeNode?.id
    const certContent = uploadedCert ?? idpData?.certificates

    const certificate = formatCert(certContent)

    if (tsg_id) {
      setLoading(true)

      const payload = {
        isActive: idpData?.isActive,
        type: LABEL_IDP_CONFIG_TYPE,
        config: {
          ssoServiceUrl,
          certificate,
          entityId
        },
      }
      try {
        await UpdateFederationIdp(tsg_id, domain, idpId, payload)
        closeModal()
        await fetchData({ tsg_id, loading: false, enableSuccessBanner: true })
        setLoading(false)
      }
      catch (e) {
        setError(true)
        setLoading(false)
      }
    }
    setLoading(false)
  }, [idpData, idpId, setError, selectedTreeNode?.id, setLoading, fetchData, domain, uploadedCert, closeModal])

  const onHandleSubmit = useCallback(async ({ identity_provider_id: entityId, identity_provider_login_url: ssoServiceUrl }) => {
    const tsg_id = selectedTreeNode?.id
    const certContent = uploadedCert ?? samlMetadata?.certificate

    const certificate = formatCert(certContent)

    if (tsg_id) {
      setLoading(true)
      const config = {
        config: {
          ssoServiceUrl,
          certificate,
          entityId
        },
        type: LABEL_IDP_CONFIG_TYPE,
      }
      try {
        await CreateFederationIdp(tsg_id, domain, config)
        closeModal()
        await fetchData({ tsg_id, loading: false, enableSuccessBanner: true })
        setLoading(false)
      }
      catch (e) {
        setError(true)
        setLoading(false)
      }
    }
    setLoading(false)
  }, [samlMetadata, setError, selectedTreeNode?.id, setLoading, fetchData, domain, uploadedCert, closeModal])

  const onSubmit = isEdit ? onHandleEdit : onHandleSubmit

  return (
    <div className='tw-relative tw-h-full tw-w-full'>
      <div className='tw-absolute tw-inset-0'>
        <Form mode='onBlur' { ...{ onSubmit } } formProps={{ style: { height: '100%', width: '100%', position: 'relative' } }}>
          {loading && <ScreenLoader appearance='light'/> }
          <FormContent {...{ uploadedCert, onBackPressed, error, uploadedFileName, samlMetadata, handleCertificateUpload, handleCertificateUploadChange, hiddenCertificateInput, loading, idpData }}/>
        </Form>
      </div>
    </div>
  )
}

ProvideSAMLMetadata.propTypes = {}
export default ProvideSAMLMetadata

